"use client";

import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/utils/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TaskAssigneeSelect } from "@/components/TaskAssigneeSelect";
import { Clock } from "lucide-react";
import { showSuccess, showError } from "@/utils/toast";

const schema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  status: z.enum(["todo", "in_progress", "done"]),
  due_date: z.string().optional(),
  project_id: z.string().min(1, "Project is required"),
  assignee_id: z.string().optional(),
  recurring: z.boolean().default(false),
  recurrence_interval: z.enum(["daily", "weekly", "monthly"]).optional(),
  recurrence_count: z.number().min(1).max(100).optional(),
});

type FormData = z.infer<typeof schema>;

interface TaskFormProps {
  onSuccess?: () => void;
  projectId?: string;
  initialData?: Partial<FormData>;
}

export const TaskForm: React.FC<TaskFormProps> = ({
  projectId,
  initialData,
  onSuccess,
}) => {
  const { profile } = useAuth();
  const { company } = useAuth();
  const queryClient = useQueryClient();
  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      ...initialData,
      project_id: projectId || initialData?.project_id || "",
      status: "todo",
      assignee_id: initialData?.assignee_id,
      recurring: false,
      recurrence_interval: "daily",
      recurrence_count: 1,
    },
  });

  // Fetch company projects (fixed: enabled guard)
  const { data: projects } = useQuery({
    queryKey: ["projects", profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];
      const { data } = await supabase
        .from("projects")
        .select("id, name")
        .eq("company_id", profile.company_id)
        .order("name");
      return data || [];
    },
    enabled: !!profile?.company_id,
  });

  const mutation = useMutation({
    mutationFn: async (data: FormData) => {
      if (!profile?.company_id) throw new Error("No company");
      const { error, data: result } = await supabase
        .from("tasks")
        .upsert({
          ...data,
          company_id: profile.company_id, // Fixed: Always set company_id
          recurring: data.recurring,
          recurrence_interval: data.recurring ? data.recurrence_interval : null,
          recurrence_count: data.recurring ? data.recurrence_count : null,
        })
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      showSuccess("Task created/updated!");
      form.reset();
      onSuccess?.();
    },
    onError: (error: any) => {
      showError("Failed to save task: " + error.message);
    },
  });

  const handleSubmit = (data: FormData) => {
    mutation.mutate(data);
  };

  return (
    
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input
                    id="title"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    required
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea
                    id="description"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="project_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Project</FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects?.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="assignee_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Assignee</FormLabel>
                <FormControl>
                  <TaskAssigneeSelect value={field.value || ""} onChange={field.onChange} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todo">To Do</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="done">Done</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="due_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Due Date</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex items-center space-x-2">
            <Switch
              id="recurring"
              checked={form.watch('recurring')}
              onCheckedChange={(checked) => form.setValue('recurring', checked)}
            />
            <FormLabel htmlFor="recurring">Recurring Task</FormLabel>
          </div>
          {form.watch('recurring') && (
            <>
              <FormField
                control={form.control}
                name="recurrence_interval"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Repeat Every</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select interval" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="recurrence_count"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Number of Times</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        max={100}
                        value={field.value ?? 1}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}
          <Button type="submit" className="w-full">Save Task</Button>
        </form>
      </Form>
    
  );
};